/**
 * Get dynamic sub-items for navigation items based on current route
 * Returns RFQ navigation items when on RFQ detail pages and parent is 'open-rfqs'
 * Returns Company navigation items when on company detail pages and parent is 'companies'
 */
export const getDynamicSubItems = (parentId: string, currentRoute: string, permissions: any = {}, userRole?: string, userCompanyPlan?: string) => {
  console.log('🔍 getDynamicSubItems called:', { parentId, currentRoute, permissions, userRole });

  // Remove query parameters for matching
  const routeWithoutQuery = currentRoute.split('?')[0];
  console.log('🔍 getDynamicSubItems - routeWithoutQuery:', routeWithoutQuery);

  // Handle Company navigation
  if (parentId === 'customer-list') {
    // Check if we're on a company detail page
    const companyMatch = routeWithoutQuery.match(/\/dashboard\/companies\/([^/]+)/);
    const companyId = companyMatch?.[1];
    console.log('🔍 getDynamicSubItems - companyId:', companyId);

    if (!companyId) {
      console.log('🔍 getDynamicSubItems - no companyId found');
      return undefined; // Not on company detail page, don't show sub-items
    }

    // Return Company navigation items
    return [
      {
        id: 'company-users',
        label: 'Users',
        route: `/dashboard/companies/${companyId}/users`,
        icon: '●'
      },
      {
        id: 'company-products',
        label: 'Products',
        route: `/dashboard/companies/${companyId}/products`,
        icon: '●'
      },
      {
        id: 'company-product-details',
        label: 'Product Details',
        route: `/dashboard/companies/${companyId}/product-details`,
        icon: '●'
      },
      {
        id: 'company-suppliers',
        label: 'Suppliers',
        route: `/dashboard/companies/${companyId}/suppliers`,
        icon: '●'
      },
      {
        id: 'company-user-rights',
        label: 'User Rights',
        route: `/dashboard/companies/${companyId}/user-rights`,
        icon: '●'
      }
    ];
  }

  // Handle RFQ navigation (existing logic)
  // Support both admin 'open-rfqs' and user 'inbox-open' parent IDs
  if (parentId !== 'open-rfqs' && parentId !== 'inbox-open') {
    console.log('🔍 getDynamicSubItems - parentId mismatch');
    return undefined;
  }

  // Check if we're on an RFQ detail page
  const rfqDetailMatch = routeWithoutQuery.match(/\/dashboard\/rfqs\/open\/([^/]+)/);
  const userRfqMatch = routeWithoutQuery.match(/\/dashboard\/user\/rfq\/([^/]+)/);
  const adminRequestMatch = routeWithoutQuery.match(/\/dashboard\/admin\/request\/([^/]+)/);

  const requestId = rfqDetailMatch?.[1] || userRfqMatch?.[1] || adminRequestMatch?.[1];
  console.log('🔍 getDynamicSubItems - requestId:', requestId);

  if (!requestId) {
    console.log('🔍 getDynamicSubItems - no requestId found');
    return undefined; // Not on RFQ detail page, don't show sub-items
  }

  // Determine base route (use routeWithoutQuery for checking)
  const isBasicPlanRoute = routeWithoutQuery.startsWith('/dashboard/user/rfq/');
  const isAdminRoute = routeWithoutQuery.startsWith('/dashboard/admin/request/') || routeWithoutQuery.startsWith('/dashboard/rfqs/open/');
  const baseRoute = isBasicPlanRoute ? '/dashboard/user/rfq' : isAdminRoute ? '/dashboard/rfqs/open' : '/dashboard/rfqs/open';

  console.log('🔍 getDynamicSubItems - baseRoute:', baseRoute, 'isBasicPlanRoute:', isBasicPlanRoute);

  // Return RFQ navigation items
  const items = [
    {
      id: 'rfq-detail',
      label: 'RFQ Review',
      route: `${baseRoute}/${requestId}`,
      icon: '●'
    }
  ];

  // Product Overview only for System Admins
  if (userRole === 'admin') {
    items.push({
      id: 'product-overview',
      label: 'Product Overview',
      route: `${baseRoute}/${requestId}/products`,
      icon: '●'
    });
  }

  // Check additional permissions for Communication/Supplier Search/Quotes
  // Logic:
  // 1. System Admin (role === 'admin') -> Always True
  // 2. Others (User, Company Admin) -> Check specific permissions AND userCompanyPlan !== 'pro'

  let hasRfqOperations = false;

  // Check if System Admin
  if (userRole === 'admin') {
    hasRfqOperations = true;
  } else {
    // Non-admins:
    // 1. Must NOT be Pro plan
    // 2. Must have permission
    const isProPlan = userCompanyPlan === 'pro';

    if (!isProPlan && permissions) {
      if (Array.isArray(permissions)) {
        // Handle array format (e.g. ['rfq_operations', 'all'])
        hasRfqOperations = permissions.includes('rfq_operations') || permissions.includes('all');
      } else if (typeof permissions === 'object') {
        // Handle object format (e.g. { rfq_operations: true })
        hasRfqOperations = permissions.rfq_operations === true || permissions.rfq_operations === 'Yes';
      }
    }
  }

  console.log('🔍 getDynamicSubItems - hasRfqOperations:', hasRfqOperations, 'permissions:', permissions, 'userRole:', userRole, 'userCompanyPlan:', userCompanyPlan);

  if (hasRfqOperations) {
    items.push(
      {
        id: 'supplier-selection',
        label: 'Search Suppliers',
        route: `${baseRoute}/${requestId}/suppliers`,
        icon: '●'
      },
      {
        id: 'communication',
        label: 'Communication',
        route: `${baseRoute}/${requestId}/communication`,
        icon: '●'
      },
      {
        id: 'quotes-review',
        label: 'Quotes Review',
        route: `${baseRoute}/${requestId}/quotes-review`,
        icon: '●'
      }
    );
  }

  return items;
};








